import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** How long the quantity limit notice stays up after an attempt past the cap, in ms. */
const QUANTITY_LIMIT_NOTICE_DURATION = 5000;

/**
 * How long the notice stays up after an add from outside the cart pushed a line past the cap, in ms.
 * Longer, because the customer's eye is still on the drawer sliding in and the line re-rendering.
 */
const QUANTITY_LIMIT_EXTERNAL_NOTICE_DURATION = 8000;

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('cart-quantity-limit', this.#handleQuantityLimit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('cart-quantity-limit', this.#handleQuantityLimit);
  }

  /**
   * Row keys whose quantity limit notice is showing, mapped to their hide timer.
   * Keyed by row key rather than line index so a notice survives a re-render. Shopify issues a
   * new key when a line's quantity changes, so `#rekeyQuantityLimitNotice` carries the entry over.
   * @type {Map<string, { timer: number }>}
   */
  #limitNotices = new Map();

  /**
   * Shows a line's quantity limit notice when the cart quantity selector reports an attempt past the cap.
   * @param {Event} event - The cart-quantity-limit event from a cart quantity selector.
   */
  #handleQuantityLimit = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const { line } = event.detail ?? {};
    const key = this.querySelectorAll('[ref="cartItemRows[]"]')[line - 1]?.getAttribute('data-key');
    if (key) this.#showQuantityLimit(key);
  };

  /**
   * Reveals the quantity limit notice for a row, replays its nudge and restarts its hide timer.
   * @param {string} key - The cart line item key.
   */
  #showQuantityLimit(key, duration = QUANTITY_LIMIT_NOTICE_DURATION) {
    const entry = this.#limitNotices.get(key) ?? { timer: 0 };
    clearTimeout(entry.timer);
    entry.timer = 0;
    this.#limitNotices.set(key, entry);

    // The countdown waits for the drawer to be on screen, so it never runs while the drawer
    // is still closed or opening. The entry, not the key, identifies the notice when it fires.
    this.#whenDrawerOpen(() => {
      if (![...this.#limitNotices.values()].includes(entry)) return;
      clearTimeout(entry.timer);
      entry.timer = window.setTimeout(() => {
        const currentKey = [...this.#limitNotices].find(([, value]) => value === entry)?.[0];
        if (currentKey) this.#hideQuantityLimit(currentKey);
      }, duration);
    });

    const limitContainer = this.#getLimitContainer(key);
    if (!limitContainer) return;

    limitContainer.classList.remove('is-hiding');
    limitContainer.hidden = false;

    const notice = limitContainer.querySelector('.cart-item__limit');
    if (!(notice instanceof HTMLElement)) return;

    notice.classList.remove('cart-item__limit--nudge');
    // Force a reflow so the animation restarts on repeated attempts
    void notice.offsetWidth;
    notice.classList.add('cart-item__limit--nudge');
  }

  /**
   * Fades out and hides a row's quantity limit notice.
   * @param {string} key - The cart line item key.
   */
  #hideQuantityLimit(key) {
    this.#limitNotices.delete(key);

    const limitContainer = this.#getLimitContainer(key);
    if (!limitContainer || limitContainer.hidden) return;

    limitContainer.classList.add('is-hiding');
    window.setTimeout(() => {
      if (this.#limitNotices.has(key)) return;
      limitContainer.hidden = true;
      limitContainer.classList.remove('is-hiding');
    }, prefersReducedMotion() ? 0 : 300);
  }

  /**
   * Moves a notice to a line's new key after a quantity change re-keyed the line.
   * @param {string | undefined} oldKey - The line's key before the change.
   * @param {number} line - The 1-based line index that changed.
   */
  #rekeyQuantityLimitNotice(oldKey, line) {
    const entry = oldKey && this.#limitNotices.get(oldKey);
    if (!entry) return;

    const newKey = this.querySelectorAll('[ref="cartItemRows[]"]')[line - 1]?.getAttribute('data-key');
    if (!newKey || newKey === oldKey) return;

    this.#limitNotices.delete(oldKey);
    this.#limitNotices.set(newKey, entry);
  }

  /**
   * Matches every row's notice to `#limitNotices` after a re-render. Morph leaves the notice's own
   * attributes alone (`data-skip-node-update`), so a row reused for a different line can carry
   * another line's state; only rows that disagree are touched, so a showing notice never toggles.
   */
  #syncQuantityLimitNotices() {
    for (const limitContainer of this.querySelectorAll('.cart-items__limit')) {
      if (!(limitContainer instanceof HTMLElement)) continue;

      const key = limitContainer.closest('[ref="cartItemRows[]"]')?.getAttribute('data-key');
      const showing = Boolean(key && this.#limitNotices.has(key));

      if (showing && limitContainer.hidden) {
        limitContainer.classList.remove('is-hiding');
        limitContainer.hidden = false;
      } else if (!showing && !limitContainer.hidden && !limitContainer.classList.contains('is-hiding')) {
        limitContainer.hidden = true;
      }
    }
  }

  /**
   * Runs a callback now, or once the cart drawer opens when this component sits in a closed one.
   * @param {() => void} callback
   */
  #whenDrawerOpen(callback) {
    const dialog = this.closest('dialog');
    const drawer = this.closest('cart-drawer-component');
    if (!dialog || !drawer || (dialog instanceof HTMLDialogElement && dialog.open)) return callback();

    drawer.addEventListener('dialog:open', callback, { once: true });
  }

  /**
   * @param {string} key - The cart line item key.
   * @returns {HTMLElement | null} The row's quantity limit notice cell.
   */
  #getLimitContainer(key) {
    const row = this.querySelector(`[ref="cartItemRows[]"][data-key="${CSS.escape(key)}"]`);
    const limitContainer = row?.querySelector('.cart-items__limit');
    return limitContainer instanceof HTMLElement ? limitContainer : null;
  }

  /**
   * After an add from outside the cart (PDP, quick view, offer module), brings any line that
   * went past the cap back down to it and shows the notice on that line.
   */
  #enforceQuantityLimit() {
    this.querySelectorAll('[ref="cartItemRows[]"]').forEach((row) => {
      const key = row.getAttribute('data-key');
      const input = row.querySelector('input[data-cart-line]');
      if (!key || !(input instanceof HTMLInputElement) || !row.querySelector('.cart-items__limit')) return;

      const max = parseInt(input.getAttribute('max') ?? '', 10);
      const quantity = parseInt(input.value, 10);
      if (Number.isNaN(max) || Number.isNaN(quantity) || quantity <= max) return;

      this.#showQuantityLimit(key, QUANTITY_LIMIT_EXTERNAL_NOTICE_DURATION);
      this.updateQuantity({ line: Number(input.dataset.cartLine), quantity: max, action: 'change' });
    });
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector('text-component')
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      ),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });

  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount =
          newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        const keyBeforeChange =
          this.querySelectorAll('[ref="cartItemRows[]"]')[line - 1]?.getAttribute('data-key') ?? undefined;

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
        this.#rekeyQuantityLimitNotice(keyBeforeChange, line);
        this.#syncQuantityLimitNotices();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#enforceQuantityLimit();
      this.#syncQuantityLimitNotices();
    } else {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false })
        .then(() => {
          this.#enforceQuantityLimit();
          this.#syncQuantityLimitNotices();
        });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
